/**
 * Ephemeral password holder for the signup -> confirm auto-signin bridge.
 *
 * Lives in JS module scope (per-tab, per-session, not persisted to storage).
 * Cleared right after the confirm endpoint returns tokens.
 *
 * Why not sessionStorage/localStorage: passwords should never touch a
 * disk-backed store, even briefly. Module scope survives client-side
 * router navigation between /signup -> /confirm but dies with the tab.
 *
 * If the user opens the confirm email link in a DIFFERENT tab or reloads
 * /confirm, this returns null and we degrade gracefully to the manual
 * sign-in flow (the confirm endpoint accepts an empty body just fine).
 */

let pending: { email: string; password: string } | null = null;

export function setPendingCredentials(email: string, password: string): void {
  pending = { email, password };
}

/**
 * Consumes the credentials (read-once). Subsequent calls return null.
 * Only returns the password if the stored email matches the caller-supplied
 * one — guards against a stale value from a prior signup.
 */
export function takePendingCredentials(email: string): { email: string; password: string } | null {
  if (!pending) return null;
  if (pending.email.trim().toLowerCase() !== email.trim().toLowerCase()) {
    // Email mismatch — different user; drop stale value.
    pending = null;
    return null;
  }
  const out = pending;
  pending = null;
  return out;
}

/** Manually clear (e.g. after user logs out or on beforeunload). */
export function clearPendingCredentials(): void {
  pending = null;
}
